import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, X, Check } from 'lucide-react';
import api from '../../services/api';
import Modal from '../../components/shared/Modal';

const STATUS_COLORS = {
  disponivel: { bg: 'bg-green-500', text: 'text-white', label: 'Disponível' },
  reservada: { bg: 'bg-yellow-400', text: 'text-gray-800', label: 'Reservada' },
  vendida: { bg: 'bg-red-500', text: 'text-white', label: 'Vendida' },
  distratada: { bg: 'bg-gray-400', text: 'text-white', label: 'Distratada' },
  permuta: { bg: 'bg-purple-400', text: 'text-white', label: 'Permuta' },
};

const STATUS_OPTIONS = [
  { value: 'disponivel', label: 'Disponível' },
  { value: 'reservada', label: 'Reservada' },
  { value: 'vendida', label: 'Vendida' },
  { value: 'distratada', label: 'Distratada' },
  { value: 'permuta', label: 'Permuta' },
];

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary';

export default function EspelhoVendas() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [torreSelecionada, setTorreSelecionada] = useState(null);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErro, setEditErro] = useState('');

  const { data: empData } = useQuery({
    queryKey: ['empreendimento', id],
    queryFn: () => api.get(`/empreendimentos`).then(r => r.data.data?.find(e => e.id === id)),
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['espelho', id],
    queryFn: () => api.get(`/empreendimentos/${id}/espelho`).then(r => r.data.data),
    refetchInterval: 30000,
  });

  const torres = Object.keys(data?.espelho || {});
  useEffect(() => { if (torres.length && !torreSelecionada) setTorreSelecionada(torres[0]); }, [torres]);

  const updateStatus = useMutation({
    mutationFn: ({ uid, status }) => api.put(`/unidades/${uid}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries(['espelho', id]); setUnidadeSelecionada(null); },
  });

  const updateUnidade = useMutation({
    mutationFn: ({ uid, ...body }) => api.put(`/unidades/${uid}`, body).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries(['espelho', id]);
      setEditMode(false);
      setUnidadeSelecionada(null);
      setEditErro('');
    },
    onError: (err) => setEditErro(err.response?.data?.error || 'Erro ao salvar'),
  });

  const espelhoTorre = torreSelecionada ? (data?.espelho?.[torreSelecionada] || {}) : {};
  const andares = Object.keys(espelhoTorre).map(Number).sort((a, b) => b - a);

  function abrirUnidade(unidade) {
    setUnidadeSelecionada(unidade);
    setEditMode(false);
    setEditErro('');
    setEditForm({
      identificador: unidade.identificador,
      area_privativa: unidade.area_privativa || '',
      preco: unidade.preco || '',
      num_vagas: unidade.num_vagas ?? '',
      status: unidade.status,
    });
  }

  function toggleEditMode() {
    if (editMode) {
      // Salvar alterações
      updateUnidade.mutate({ uid: unidadeSelecionada.id, ...editForm });
    } else {
      // Entrar em modo edição
      setEditMode(true);
    }
  }

  function setEdit(k) { return e => setEditForm(f => ({ ...f, [k]: e.target.value })); }

  if (isLoading) return <div className="p-8 text-center text-gray-400">Carregando espelho...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/empreendimentos')} className="text-gray-500 hover:text-gray-700"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Espelho de Vendas</h1>
          {empData && <p className="text-gray-500 text-sm">{empData.nome}</p>}
        </div>
      </div>

      {/* Stats */}
      {data?.stats && (
        <div className="flex gap-4 flex-wrap">
          {Object.entries(STATUS_COLORS).map(([status, cfg]) => (
            <div key={status} className={`${cfg.bg} ${cfg.text} px-4 py-2 rounded-lg text-sm font-medium`}>
              {cfg.label}: {data.stats[status] || 0}
            </div>
          ))}
          <div className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">Total: {data.stats.total}</div>
        </div>
      )}

      {/* Abas de Torres */}
      {torres.length > 1 && (
        <div className="flex gap-2">
          {torres.map(torre => (
            <button key={torre}
              onClick={() => setTorreSelecionada(torre)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${torreSelecionada === torre ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {torre}
            </button>
          ))}
        </div>
      )}

      {/* Legenda */}
      <div className="flex gap-4 flex-wrap bg-white rounded-lg p-3 shadow-sm">
        {Object.entries(STATUS_COLORS).map(([s, cfg]) => (
          <div key={s} className="flex items-center gap-2 text-xs">
            <div className={`w-4 h-4 rounded ${cfg.bg}`} />
            <span>{cfg.label}</span>
          </div>
        ))}
      </div>

      {/* Grid Espelho */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-xs text-gray-500 w-16">Andar</th>
              {(espelhoTorre[andares[0]] || []).map((_, i) => (
                <th key={i} className="px-2 py-3 text-center text-xs text-gray-500">Pos. {String(i + 1).padStart(2, '0')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {andares.map(andar => (
              <tr key={andar} className="border-t">
                <td className="px-4 py-3 font-semibold text-gray-700 bg-gray-50 text-center">{andar}°</td>
                {(espelhoTorre[andar] || []).map(unidade => {
                  const cfg = STATUS_COLORS[unidade.status] || STATUS_COLORS.disponivel;
                  const isSelected = unidadeSelecionada?.id === unidade.id;
                  return (
                    <td key={unidade.id} className="px-1 py-2 text-center">
                      <button
                        onClick={() => abrirUnidade(unidade)}
                        className={`${cfg.bg} ${cfg.text} w-full rounded-lg py-3 px-2 font-medium text-xs hover:opacity-80 transition-opacity ${isSelected ? 'ring-2 ring-offset-2 ring-primary' : ''}`}
                      >
                        <div>{unidade.identificador}</div>
                        {unidade.area_privativa && <div className="opacity-75">{unidade.area_privativa}m²</div>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Unidade */}
      {unidadeSelecionada && (
        <Modal isOpen={!!unidadeSelecionada} onClose={() => { setUnidadeSelecionada(null); setEditMode(false); setEditErro(''); }}
          title={editMode ? `Editar — ${unidadeSelecionada.identificador}` : `Unidade ${unidadeSelecionada.identificador}`}
          size="lg">
          <div className="space-y-4">
            {editMode ? (
              // Modo Edição
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Identificador</label>
                    <input value={editForm.identificador || ''} onChange={setEdit('identificador')} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select value={editForm.status || ''} onChange={setEdit('status')} className={inputCls}>
                      {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Área Privativa (m²)</label>
                    <input type="number" step="0.01" min="0" value={editForm.area_privativa || ''} onChange={setEdit('area_privativa')} className={inputCls} placeholder="72.50" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vagas de Garagem</label>
                    <input type="number" min="0" value={editForm.num_vagas ?? ''} onChange={setEdit('num_vagas')} className={inputCls} placeholder="1" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor (R$)</label>
                    <input type="number" step="1" min="0" value={editForm.preco || ''} onChange={setEdit('preco')} className={inputCls} placeholder="350000" />
                  </div>
                </div>
                {editErro && <p className="text-red-500 text-sm">{editErro}</p>}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button onClick={() => { setEditMode(false); setEditErro(''); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cancelar
                  </button>
                  <button onClick={toggleEditMode} disabled={updateUnidade.isPending}
                    className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary disabled:opacity-50">
                    {updateUnidade.isPending ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </div>
              </div>
            ) : (
              // Modo Visualização
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Status</p>
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium mt-1 ${STATUS_COLORS[unidadeSelecionada.status]?.bg} ${STATUS_COLORS[unidadeSelecionada.status]?.text}`}>
                      {STATUS_COLORS[unidadeSelecionada.status]?.label}
                    </span>
                  </div>
                  <div><p className="text-gray-400">Tipologia</p><p className="font-medium mt-1">{unidadeSelecionada.tipologia_nome || '—'}</p></div>
                  <div><p className="text-gray-400">Área</p><p className="font-medium mt-1">{unidadeSelecionada.area_privativa ? `${unidadeSelecionada.area_privativa}m²` : '—'}</p></div>
                  <div><p className="text-gray-400">Vagas</p><p className="font-medium mt-1">{unidadeSelecionada.num_vagas ?? '—'}</p></div>
                  {unidadeSelecionada.preco && (
                    <div>
                      <p className="text-gray-400">Preço</p>
                      <p className="font-medium mt-1">
                        {unidadeSelecionada.preco_cheio && parseFloat(unidadeSelecionada.preco_cheio) > parseFloat(unidadeSelecionada.preco) && (
                          <span className="line-through text-gray-400 mr-1">R$ {parseFloat(unidadeSelecionada.preco_cheio).toLocaleString('pt-BR')}</span>
                        )}
                        R$ {parseFloat(unidadeSelecionada.preco).toLocaleString('pt-BR')}
                        {unidadeSelecionada.preco_cheio && parseFloat(unidadeSelecionada.preco_cheio) > parseFloat(unidadeSelecionada.preco) && (
                          <span className="ml-1 text-xs text-green-600">promoção</span>
                        )}
                      </p>
                    </div>
                  )}
                  {unidadeSelecionada.comprador_nome && (
                    <div><p className="text-gray-400">Comprador</p><p className="font-medium mt-1">{unidadeSelecionada.comprador_nome}</p></div>
                  )}
                  {unidadeSelecionada.reservada_para && (
                    <div><p className="text-gray-400">Reservada para</p><p className="font-medium mt-1">{unidadeSelecionada.reservada_para}</p></div>
                  )}
                  {unidadeSelecionada.reserva_por && (
                    <div><p className="text-gray-400">Reserva feita por</p><p className="font-medium mt-1">{unidadeSelecionada.reserva_por}</p></div>
                  )}
                </div>

                {unidadeSelecionada.observacoes && (
                  <div className="text-sm">
                    <p className="text-gray-400">Observações</p>
                    <p className="mt-1 whitespace-pre-wrap">{unidadeSelecionada.observacoes}</p>
                  </div>
                )}

                {/* Botões de ação */}
                <div className="pt-4 border-t space-y-2">
                  <button onClick={toggleEditMode}
                    className="w-full flex items-center justify-center gap-2 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary">
                    <Pencil size={14} /> Editar Unidade
                  </button>

                  {unidadeSelecionada.status === 'disponivel' && (
                    <div className="space-y-2 pt-2">
                      <p className="text-sm font-medium text-gray-700">Mudar status:</p>
                      <button onClick={() => updateStatus.mutate({ uid: unidadeSelecionada.id, status: 'reservada' })}
                        className="w-full py-2 bg-yellow-400 text-gray-800 rounded-lg text-sm font-medium hover:bg-yellow-500">
                        Marcar como Reservada
                      </button>
                      <button onClick={() => updateStatus.mutate({ uid: unidadeSelecionada.id, status: 'vendida' })}
                        className="w-full py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600">
                        Marcar como Vendida
                      </button>
                    </div>
                  )}

                  {unidadeSelecionada.status === 'reservada' && (
                    <div className="space-y-2 pt-2">
                      <p className="text-sm font-medium text-gray-700">Mudar status:</p>
                      <button onClick={() => updateStatus.mutate({ uid: unidadeSelecionada.id, status: 'disponivel' })}
                        className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                        Voltar para Disponível
                      </button>
                      <button onClick={() => updateStatus.mutate({ uid: unidadeSelecionada.id, status: 'vendida' })}
                        className="w-full py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600">
                        Marcar como Vendida
                      </button>
                    </div>
                  )}

                  {unidadeSelecionada.status === 'vendida' && (
                    <div className="space-y-2 pt-2">
                      <p className="text-sm font-medium text-gray-700">Mudar status:</p>
                      <button onClick={() => updateStatus.mutate({ uid: unidadeSelecionada.id, status: 'disponivel' })}
                        className="w-full py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600">
                        Voltar para Disponível
                      </button>
                      <button onClick={() => updateStatus.mutate({ uid: unidadeSelecionada.id, status: 'reservada' })}
                        className="w-full py-2 bg-yellow-400 text-gray-800 rounded-lg text-sm font-medium hover:bg-yellow-500">
                        Voltar para Reservada
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
